// ── GRANT HELPERS (fp-nofodesk-9) — extracted from App.js ──
import { apiFetch, apiLoadTable, computeGrantId, API } from "./api";
import { formatFieldValue } from "./formatFieldValue";


function fmtUSDFull(n) { return n ? "$" + Math.round(n).toLocaleString() : "$0"; }
function parseAmt(v)   { return parseFloat(String(v || "").replace(/[^0-9.]/g,"")) || 0; }

// ── localStorage helpers ──────────────────────────────────────
const ORG_KEY = "orgProfile_v1";
function loadOrgProfile() {
  try { return JSON.parse(localStorage.getItem(ORG_KEY) || "null"); }
  catch { return null; }
}
function saveOrgProfile(profile) { localStorage.setItem(ORG_KEY, JSON.stringify(profile)); }

// ── Tracked grants (Supabase-backed) ─────────────────────────
async function apiTrackGrant(grantData) {
  const id = computeGrantId(grantData);
  const entry = {
    id,
    title:     grantData.opportunityIdentification?.callTitle || "Untitled",
    funder:    grantData.opportunityIdentification?.funderName || "Unknown",
    deadline:  grantData.timeline?.applicationDeadline || "Not specified",
    awardMax:  grantData.fundingParameters?.awardMax || "",
    currency:  grantData.fundingParameters?.currency || "USD",
    type:      grantData._classification?.primary || "Grant",
    status:    "tracking",
    addedAt:   new Date().toISOString(),
    sourceUrl: grantData.opportunityIdentification?.sourceUrl || grantData._source || "",
  };
  const current = await apiLoadTable('tracked-grants') || [];
  const idx = current.findIndex(g => g.id === id);
  const updated = idx >= 0 ? current.map(g => g.id === id ? { ...g, ...entry } : g) : [entry, ...current];
  await apiFetch(`${API}/db/tracked-grants`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ records: updated }),
  }).catch(() => {});
}

// ── Utility helpers ───────────────────────────────────────────
function daysUntilDeadline(str) {
  if (!str || str === "Not specified") return null;
  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / 86400000);
}

// 12a: all field values route through the shared object-safe formatter
// (client/src/utils/formatFieldValue.js) — same formatter as the exports.
function formatValue(v) { return formatFieldValue(v); }

function flattenData(obj, prefix = "") {
  const rows = [];
  if (!obj || typeof obj !== "object") return rows;
  Object.entries(obj).forEach(([k, v]) => {
    if (k.startsWith("_")) return;
    const label = (prefix ? prefix + " › " : "") +
      k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    if (Array.isArray(v)) { const f = formatValue(v); if (f) rows.push({ label, value: f }); }
    else if (typeof v === "object" && v !== null) rows.push(...flattenData(v, label));
    else { const f = formatValue(v); if (f) rows.push({ label, value: f }); }
  });
  return rows;
}

function groupBySection(data) {
  const groups = [];
  if (!data || typeof data !== "object") return groups;
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith("_")) return;
    if (typeof value !== "object" || value === null) return;
    const title = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    const rows = flattenData(value);
    if (rows.length > 0) groups.push({ title, rows });
  });
  return groups;
}

// ── Export column maps ────────────────────────────────────────
const ACTIVE_COLS = [
  { key:"sn", label:"S/N", w:6 }, { key:"donor", label:"Donor", w:24 },
  { key:"grantNumber", label:"Grant Number", w:20 }, { key:"pi", label:"Principal Investigator", w:24 },
  { key:"shortTitle", label:"Short Title", w:20 }, { key:"title", label:"Full Grant Name", w:48 },
  { key:"type", label:"Type of Grant", w:22 }, { key:"states", label:"States", w:22 },
  { key:"totalUSD", label:"Amount Awarded (USD)", w:20 }, { key:"fy26", label:"FY26 Funding", w:16 },
  { key:"primeOrSub", label:"Prime/Sub", w:10 }, { key:"start", label:"Start Date", w:12 },
  { key:"end", label:"End Date", w:12 }, { key:"poc", label:"POC", w:16 },
];
const APP_COLS = [
  { key:"sn", label:"S/N", w:6 }, { key:"funder", label:"Funder", w:28 },
  { key:"rfaNumber", label:"RFA Number", w:18 }, { key:"title", label:"Application Title", w:48 },
  { key:"amount", label:"Funding Amount", w:20 }, { key:"deadline", label:"Submission Deadline", w:18 },
  { key:"submittedDate", label:"Submitted Date", w:15 }, { key:"status", label:"Application Status", w:18 },
  { key:"awardStatus", label:"Award Status", w:14 }, { key:"primeOrSub", label:"Prime/Sub", w:10 },
  { key:"pi", label:"Principal Investigator", w:24 }, { key:"poc", label:"POC", w:16 },
  { key:"remark", label:"Remark", w:30 },
];
const TERMINATED_COLS = [
  { key:"sn", label:"S/N", w:6 }, { key:"funder", label:"Funder", w:24 },
  { key:"awardNumber", label:"Award Number", w:20 }, { key:"primeOrSub", label:"Prime/Sub", w:10 },
  { key:"pi", label:"PI", w:22 }, { key:"title", label:"Project Title", w:48 },
  { key:"awardedAmount", label:"Awarded Amount", w:18 }, { key:"duration", label:"Duration", w:14 },
  { key:"year", label:"Year", w:10 }, { key:"remarks", label:"Remarks", w:30 },
  { key:"poc1", label:"POC 1", w:14 }, { key:"poc2", label:"POC 2", w:14 },
];
const COMPLETED_COLS = [
  { key:"sn", label:"S/N", w:6 }, { key:"funder", label:"Funder", w:24 },
  { key:"awardNumber", label:"Award Number", w:20 }, { key:"primeOrSub", label:"Prime/Sub", w:10 },
  { key:"pi", label:"PI", w:22 }, { key:"title", label:"Project Title", w:48 },
  { key:"awardedAmount", label:"Awarded Amount", w:18 }, { key:"duration", label:"Duration", w:14 },
  { key:"year", label:"Year", w:10 }, { key:"poc1", label:"POC 1", w:14 }, { key:"poc2", label:"POC 2", w:14 },
];

// ── Dashboard helpers ─────────────────────────────────────────
function parseUSD(str) {
  if (!str || typeof str !== "string") return 0;
  if (!str.startsWith("$")) return 0;
  return parseFloat(str.replace(/[$,]/g, "")) || 0;
}
function formatUSD(n) {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

// ── File parsers ──────────────────────────────────────────────
async function parsePDF(file) {
  const mod = await import('pdfjs-dist');
  const pdfjsLib = mod.default || mod;
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
  let text = '';
  for (let i = 1; i <= Math.min(pdf.numPages, 50); i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(s => s.str).join(' ') + '\n';
  }
  return text.trim();
}
async function parseDocx(file) {
  const fd = new FormData(); fd.append("file", file);
  const res = await apiFetch(`${API}/parse-docx`, { method: "POST", body: fd });
  if (!res.ok) throw new Error("DOCX parsing failed");
  return (await res.json()).text;
}
async function parseExcel(file) {
  const fd = new FormData(); fd.append("file", file);
  const res = await apiFetch(`${API}/parse-excel`, { method: "POST", body: fd });
  if (!res.ok) throw new Error("Excel parsing failed");
  return (await res.json()).text;
}

export { fmtUSDFull, parseAmt, ORG_KEY, loadOrgProfile, saveOrgProfile, apiTrackGrant, daysUntilDeadline, formatValue, flattenData, groupBySection, ACTIVE_COLS, APP_COLS, TERMINATED_COLS, COMPLETED_COLS, parseUSD, formatUSD, parsePDF, parseDocx, parseExcel };
